import { useT } from '@/lib/i18n';
import { PageLoader } from '@/components/ui/loader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Download, X } from 'lucide-react';
import { ApiRequestError, apiGet, apiPost } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { PublicForm } from './api';

/** The visitor id the tracking snippet uses, so a form ties back to the browsing. */
export function visitorKey(): string {
  try {
    let key = localStorage.getItem('dovo_vid');
    if (!key) {
      key = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
      localStorage.setItem('dovo_vid', key);
    }
    return key;
  } catch {
    return Math.random().toString(36).slice(2).padEnd(12, '0');
  }
}

type Done = { message: string; redirectUrl: string | null; downloadUrl: string | null };

/**
 * A form as a visitor sees it — on its own page, inside an iframe on a
 * customer's site, in a pop-up, or as a block on a landing page.
 */
export function FormView({
  form,
  landingPageId,
  pageUrl,
  referrer,
  vid,
  compact = false,
  onSubmitted,
}: {
  form: PublicForm;
  landingPageId?: string;
  pageUrl?: string;
  referrer?: string;
  vid?: string;
  compact?: boolean;
  onSubmitted?: () => void;
}) {
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<Done | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const opened = useRef(Date.now());
  const t = useT();
  const colour = form.settings.colour || '#2563eb';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    setErrors({});
    try {
      const data: Record<string, string | boolean> = { ...values };
      for (const field of form.fields) if (field.type === 'hidden' && field.defaultValue) data[field.key] = field.defaultValue;
      const result = await apiPost<Done>(`/public/forms/${form.key}/submit`, {
        data,
        pageUrl: pageUrl ?? window.location.href,
        referrer: referrer ?? document.referrer,
        visitorKey: vid ?? visitorKey(),
        landingPageId,
        consent,
        website: honeypot,
        elapsed: Date.now() - opened.current,
      });
      setDone(result);
      onSubmitted?.();
      if (result.redirectUrl) setTimeout(() => (window.top ?? window).location.assign(result.redirectUrl!), 1_200);
    } catch (failure) {
      if (failure instanceof ApiRequestError) {
        setErrors(failure.fieldErrors);
        setError(failure.message);
      } else setError('Could not send — please try again.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="py-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: colour }} />
        <p className="mt-3 whitespace-pre-wrap text-lg font-semibold text-slate-900">{done.message}</p>
        {done.downloadUrl && (
          <a
            href={done.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white"
            style={{ backgroundColor: colour }}
          >
            <Download className="h-4 w-4" /> {t('Download')}
          </a>
        )}
      </div>
    );
  }

  const input =
    'w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition focus:border-transparent focus:ring-2';

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {(form.settings.title || form.settings.description) && !compact && (
        <div>
          {form.settings.title && <h2 className="text-xl font-extrabold text-slate-900">{form.settings.title}</h2>}
          {form.settings.description && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{form.settings.description}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {form.fields
          .filter((field) => field.type !== 'hidden')
          .map((field) => (
            <div key={field.id} className={cn('space-y-1.5', field.width === 'half' ? 'col-span-2 sm:col-span-1' : 'col-span-2')}>
              {field.type !== 'checkbox' && (
                <label htmlFor={`f-${field.id}`} className="block text-sm font-medium text-slate-700">
                  {field.label}
                  {field.required && <span className="text-red-500"> *</span>}
                </label>
              )}
              {field.type === 'textarea' ? (
                <textarea
                  id={`f-${field.id}`}
                  rows={4}
                  className={input}
                  style={{ ['--tw-ring-color' as string]: colour }}
                  placeholder={field.placeholder ?? undefined}
                  value={String(values[field.key] ?? '')}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                />
              ) : field.type === 'select' ? (
                <select
                  id={`f-${field.id}`}
                  className={input}
                  style={{ ['--tw-ring-color' as string]: colour }}
                  value={String(values[field.key] ?? '')}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                >
                  <option value="">{field.placeholder || t('Choose…')}</option>
                  {field.options?.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              ) : field.type === 'checkbox' ? (
                <label className="flex items-start gap-2.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    style={{ accentColor: colour }}
                    checked={Boolean(values[field.key])}
                    onChange={(event) => setValues({ ...values, [field.key]: event.target.checked })}
                  />
                  <span>
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </span>
                </label>
              ) : (
                <input
                  id={`f-${field.id}`}
                  type={field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
                  autoComplete={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.key === 'fullName' ? 'name' : undefined}
                  className={input}
                  style={{ ['--tw-ring-color' as string]: colour }}
                  placeholder={field.placeholder ?? (field.type === 'phone' ? '+971 50 123 4567' : undefined)}
                  value={String(values[field.key] ?? '')}
                  onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}
                />
              )}
              {errors[field.key] && <p className="text-xs text-red-600">{errors[field.key]}</p>}
            </div>
          ))}
      </div>
      {/* People never see this; bots fill it in. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute start-[-9999px] h-0 w-0 opacity-0"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
      />
      {form.settings.consentText && (
        <label className="flex items-start gap-2.5 text-xs text-slate-600">
          <input type="checkbox" className="mt-0.5 h-4 w-4" style={{ accentColor: colour }} checked={consent} onChange={(event) => setConsent(event.target.checked)} />
          <span>{form.settings.consentText}</span>
        </label>
      )}
      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <button
        type="submit"
        disabled={sending || (form.settings.requireConsent && !consent)}
        className="w-full rounded-xl px-5 py-3 text-[15px] font-semibold text-white shadow-lg transition hover:brightness-110 disabled:opacity-60"
        style={{ backgroundColor: colour, boxShadow: `0 10px 24px -10px ${colour}` }}
      >
        {sending ? t('Sending…') : form.settings.submitLabel}
      </button>
    </form>
  );
}

/** dovocrm.com/f/<key> — on its own, embedded (?embed=1) or as a pop-up (?popup=1). */
export function PublicFormPage() {
  const { formKey = '' } = useParams();
  const [params] = useSearchParams();
  const embedded = params.get('embed') === '1' || params.get('popup') === '1';
  const popup = params.get('popup') === '1';
  const box = useRef<HTMLDivElement>(null);
  const { data: form, isError, isLoading } = useQuery({
    queryKey: ['public-form', formKey],
    queryFn: () => apiGet<PublicForm>(`/public/forms/${formKey}`),
    retry: false,
    staleTime: Infinity,
  });
  const context = useMemo(
    () => ({ pageUrl: params.get('page') ?? undefined, referrer: params.get('ref') ?? undefined, vid: params.get('vid') ?? undefined }),
    [params],
  );

  // Tell the page around the iframe how tall the form is, so it never scrolls inside.
  useEffect(() => {
    if (!embedded || !box.current) return;
    const report = () => window.parent.postMessage({ dovo: 'height', key: formKey, height: box.current?.scrollHeight ?? 0 }, '*');
    const observer = new ResizeObserver(report);
    observer.observe(box.current);
    report();
    return () => observer.disconnect();
  }, [embedded, formKey, form]);

  const close = () => window.parent.postMessage({ dovo: 'close', key: formKey }, '*');

  const body = isLoading ? (
    <PageLoader />
  ) : isError || !form ? (
    <p className="py-10 text-center text-sm text-slate-500">This form is not available.</p>
  ) : (
    <FormView
      form={form}
      pageUrl={context.pageUrl}
      referrer={context.referrer}
      vid={context.vid}
      onSubmitted={() => window.parent.postMessage({ dovo: 'submitted', key: formKey }, '*')}
    />
  );

  if (embedded) {
    return (
      <div ref={box} className={cn('relative bg-white p-5 sm:p-6', popup && 'rounded-2xl')} style={{ colorScheme: 'light' }}>
        {popup && (
          <button onClick={close} aria-label="Close" className="absolute end-3 top-3 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        )}
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12" style={{ colorScheme: 'light' }}>
      <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8">
        {form && <p className="mb-4 text-sm font-semibold text-slate-500">{form.company}</p>}
        {body}
      </div>
    </div>
  );
}
